import { Alert, AlertIcon, Box, Flex, Heading, Icon, IconButton, Text, Textarea } from "@chakra-ui/react";
import { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowUp, FiArrowUpRight, FiSearch } from "react-icons/fi";
import { FENCY_DEFAULT_MODEL } from "./fencySetup";
import { LocalizedAgentTaskProgress } from "./LocalizedAgentTaskProgress";
import { useExploreMemoriesChat } from "./useExploreMemoriesChat";

type ExploreMemoriesChatProps = {
  fetchCreateExploreMemoriesClientToken: (conversationId: string) => Promise<{ clientToken: string }>;
  fetchFencyFileDownloadClientToken: (agentTaskFileId: string) => Promise<{ clientToken: string }>;
  examplePrompts?: string[];
  /** A question asked elsewhere — the home search bar — and carried here to be answered on arrival. */
  initialPrompt?: string;
};

export const ExploreMemoriesChat = ({
  fetchCreateExploreMemoriesClientToken,
  fetchFencyFileDownloadClientToken,
  examplePrompts = [],
  initialPrompt,
}: ExploreMemoriesChatProps) => {
  const { t } = useTranslation();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { userMessages, agentTasks, prompt, setPrompt, isLoading, sendError, handleSend } = useExploreMemoriesChat({
    model: FENCY_DEFAULT_MODEL,
    fetchCreateExploreMemoriesClientToken,
    fetchFencyFileDownloadClientToken,
  });

  // Sent once, on arrival. Keyed on nothing: a second run would ask the same question twice.
  const sentInitial = useRef(false);
  useEffect(() => {
    if (!initialPrompt || sentInitial.current) return;
    sentInitial.current = true;
    void handleSend(initialPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  const stickToBottom = useRef(true);

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const observer = new MutationObserver(() => {
      if (stickToBottom.current) container.scrollTop = container.scrollHeight;
    });
    observer.observe(container, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    stickToBottom.current = true;
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [userMessages.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const isEmpty = userMessages.length === 0;

  return (
    <Flex
      flex={1}
      flexDirection="column"
      minWidth={0}
      minH={0}
      overflow="hidden"
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      rounded="xl"
    >
      <Box
        ref={messagesContainerRef}
        onScroll={handleScroll}
        flex={1}
        minHeight={0}
        overflowY="auto"
        px={{ base: "4", md: "6" }}
        py="5"
      >
        {isEmpty ? (
          <Flex flex={1} h="full" direction="column" align="center" justify="center" gap="6" py="6">
            <Flex
              boxSize="14"
              align="center"
              justify="center"
              rounded="2xl"
              bg="smBackgroundSecondary"
              color="smPrimary"
            >
              <Icon as={FiSearch} boxSize="6" />
            </Flex>

            <Box textAlign="center" maxW="md">
              <Heading as="h2" size="md" color="smText" mb="2" sx={{ textWrap: "balance" }}>
                {t("What are you looking for?")}
              </Heading>
              <Text color="smMuted" fontSize="sm" lineHeight="tall">
                {t(
                  "Ask in plain language. The agent searches your contracts — supplier, project, value and dates — and the contract documents themselves.",
                )}
              </Text>
            </Box>

            {examplePrompts.length > 0 && (
              <Box w="full" maxW="md">
                <Text
                  fontSize="11px"
                  fontWeight="semibold"
                  letterSpacing="0.06em"
                  textTransform="uppercase"
                  color="smTextDisabled"
                  mb="2"
                  px="1"
                >
                  {t("Suggestions")}
                </Text>
                <Flex direction="column" gap="2">
                  {examplePrompts.map((example) => (
                    <Flex
                      key={example}
                      as="button"
                      type="button"
                      onClick={() => void handleSend(example)}
                      align="center"
                      justify="space-between"
                      gap="3"
                      textAlign="left"
                      w="full"
                      px="4"
                      py="3"
                      rounded="lg"
                      borderWidth="1px"
                      borderColor="smBorder"
                      color="smText"
                      fontSize="sm"
                      transition="all 0.15s"
                      _hover={{ borderColor: "smPrimary", bg: "smBackgroundSecondary" }}
                      _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
                      disabled={isLoading}
                    >
                      <Text>{example}</Text>
                      <Icon as={FiArrowUpRight} color="smMuted" flexShrink={0} />
                    </Flex>
                  ))}
                </Flex>
              </Box>
            )}
          </Flex>
        ) : (
          <Flex direction="column" gap="3">
            {userMessages.map((content, i) => (
              <Fragment key={`msg-${i}`}>
                <Flex justifyContent="flex-end">
                  <Box
                    maxWidth="75%"
                    px="4"
                    py="2"
                    borderRadius="16px 16px 4px 16px"
                    backgroundColor="smPrimary"
                    color="white"
                    fontSize="sm"
                    lineHeight="tall"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                  >
                    {content}
                  </Box>
                </Flex>
                {agentTasks[i] && (
                  <Flex justifyContent="flex-start">
                    <Box maxWidth="100%" width="100%">
                      <LocalizedAgentTaskProgress agentTask={agentTasks[i]} />
                    </Box>
                  </Flex>
                )}
              </Fragment>
            ))}
            {sendError && !isLoading && (
              <Alert status="error" rounded="md" fontSize="sm">
                <AlertIcon />
                {t("The search couldn't be completed. Please try again.")}
              </Alert>
            )}
          </Flex>
        )}
      </Box>

      <Box px={{ base: "3", md: "4" }} pb="4" pt="2">
        <Flex
          align="flex-end"
          gap="2"
          bg="smBackground"
          borderWidth="1px"
          borderColor="smBorder"
          rounded="xl"
          px="3"
          py="2"
          transition="border-color 0.15s"
          _focusWithin={{ borderColor: "smPrimary" }}
        >
          <Textarea
            variant="unstyled"
            placeholder={t("Ask about your contracts and documents…") ?? ""}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            flex={1}
            minH="28px"
            maxH="160px"
            py="1.5"
            px="1"
            resize="none"
            overflowY="auto"
            isDisabled={isLoading}
          />
          <IconButton
            aria-label={t("Send") ?? "Send"}
            icon={<Icon as={FiArrowUp} boxSize="4" />}
            colorScheme="teal"
            rounded="full"
            size="sm"
            flexShrink={0}
            isLoading={isLoading}
            isDisabled={isLoading || !prompt.trim()}
            onClick={() => void handleSend()}
          />
        </Flex>
      </Box>
    </Flex>
  );
};
